/* eslint-disable ember/no-runloop */
import { set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import type { HasMany } from '@ember-data/model';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { task, timeout } from 'ember-concurrency';
import { t } from 'ember-intl';
import { COLOR_PARITY_LOSS } from 'frontend-utils/utils/css-variable';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import type { ParityInsightSegmentationItem } from 'lumen/models';
import { TOOLTIP_HOVER_TIMEOUT } from 'lumen/services/usage-analytics';
import type { ChartCustomPoint } from 'lumen/utils/constants/highcharts-constants';

import styles from './chart-issue-type.module.css';

const allowedIssues = [
  'LOSS_SOLDOUT',
  'LOSS_ROOMTYPE',
  'LOSS_PRICE_DIFFERENCE',
];

interface IssueTypePoint extends ChartCustomPoint {
  issue: ParityInsightSegmentationItem;
}

interface IssueTypeTooltipContext
  extends Highcharts.TooltipFormatterContextObject {
  point: IssueTypePoint;
}

type IssueTypeSeries = Omit<Highcharts.SeriesBarOptions, 'data'> & {
  data: { issue: ParityInsightSegmentationItem; x: number; y: number }[];
};

interface IssueTypeTooltipValues {
  singleValue: number;
  singleValueTitle: string;
  tooltipTitle: string;
  totalNbOfHotels: number;
  totalNbOfShops: number;
}

interface ParityChartsChartIssueTypeSignature {
  Args: {
    hideHotelsMetric: boolean;
    issueTypeData: HasMany<ParityInsightSegmentationItem> | undefined;
    parsedSegmentationFilters?: Record<string, string[]>;
    segmentationFilterClicked?: (
      segmentationId: string,
      segmentationValueId: string,
    ) => void;
    showLoadingPlaceholderState: boolean;
    totalLoss?: number;
  };
  Element: HTMLElement;
}

export default class ParityChartsChartIssueType extends Component<ParityChartsChartIssueTypeSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare intl: Services['intl'];

  @service declare usageAnalytics: Services['usage-analytics'];

  declare tooltipValues: IssueTypeTooltipValues;

  trackHoverTask = task({ restartable: true }, async (issueType: string) => {
    await timeout(TOOLTIP_HOVER_TIMEOUT);
    this.usageAnalytics.track('Issue Type Details Viewed', {
      eventTrigger: 'issue-type-chart',
      issueType,
    });
  });

  get chartData(): IssueTypeSeries[] {
    if (this.args.issueTypeData) {
      const allIssues = this.args.issueTypeData
        .filter(
          (issue) => allowedIssues.indexOf(issue.segmentationValueId) !== -1,
        )
        .sort((a, b) => b.calculatedValues.loss - a.calculatedValues.loss);
      const series: IssueTypeSeries[] = [];

      if (allIssues) {
        allIssues.forEach((issue, index) => {
          series.push({
            type: 'bar',
            name: issue.segmentationValueId,
            data: [
              {
                x: index,
                y: issue.calculatedValues.loss,
                issue,
              },
            ],
            color: `var(${COLOR_PARITY_LOSS})`,
            borderColor: 'var(--prism-color-border-inverse-emphasis)',
          });
        });

        return series;
      }
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        zoomType: null,
        type: 'bar',
        title: '',
        height: null, // Grow chart to fill container
        spacingLeft: 0,
        spacingRight: 0,
        spacingBottom: 0,
      },

      title: {
        text: 'parity-issuetype-chart',
      },

      legend: {
        enabled: false,
      },

      tooltip: {
        shared: false,
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shape: 'square',
        shadow: false,
        outside: true,
        distance: 20,

        formatter(this: IssueTypeTooltipContext): string {
          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();

          const tooltipValues: IssueTypeTooltipValues = {
            totalNbOfShops: this.point.issue.value.loss,
            totalNbOfHotels: this.point.issue.value.subscriptions,
            singleValue: this.point.issue.calculatedValues.loss,
            singleValueTitle: that.intl
              .t('parity_analytics.percentage_total')
              .toString(),
            tooltipTitle: that.intl
              .t(
                `parity_analytics.issue_type.${this.point.issue.segmentationValueId.toLowerCase()}`,
              )
              .toString(),
          };

          set(that, 'tooltipValues', tooltipValues);
          end();
          // @ts-expect-error: Object is possibly 'null'.
          return document.getElementById('dummy-tooltip-for-issue-type')
            .innerHTML;
        },
        useHTML: true,
      },

      xAxis: {
        minPadding: 0.5,
        maxPadding: 0.5,
        tickInterval: 1,
        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            // Return translated issue type when available
            // @ts-expect-error: Element implicitly has an 'any' type because index expression is not of type 'number'.
            if (this.chart.series[this.value]) {
              return that.intl
                .t(
                  // @ts-expect-error: Element implicitly has an 'any' type because index expression is not of type 'number'.
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                  `parity_analytics.issue_type.${this.chart.series[this.value].name.toLowerCase()}`,
                )
                .toString();
            }

            return '';
          },
        },
      },

      yAxis: {
        min: 0,
        title: {
          enabled: false,
        },
        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            // @ts-expect-error: Argument of type 'string | number' is not assignable to parameter of type 'number | bigint | StringNumericLiteral | null | undefined'.
            return that.intl.formatNumber(this.value, { style: 'percent' });
          },
        },
      },

      plotOptions: {
        series: {
          pointPadding: 0.1,
          groupPadding: 0.1,
          pointWidth: null,
          stacking: 'normal',
        },
        bar: {
          point: {
            events: {
              mouseOver(this: IssueTypePoint): void {
                void that.trackHoverTask.perform(
                  this.issue.segmentationValueId,
                );
              },
              mouseOut(): void {
                void that.trackHoverTask.cancelAll();
              },
            },
          },
        },
      },
    };
  }

  get knownIssueTypes(): string[] {
    if (this.args.issueTypeData) {
      return this.args.issueTypeData.map((issue) => issue.segmentationValueId);
    }

    return [];
  }

  <template>
    <PrismCard ...attributes>
      <:header as |Header|>
        <div class="flex items-center">
          {{#if @showLoadingPlaceholderState}}
            <p class="loading-pulse--col-3"></p>
          {{else}}
            <Header.title>
              {{t "parity_analytics.issue_type"}}
            </Header.title>
          {{/if}}
        </div>
      </:header>

      <:content>
        <div class={{styles.breakdown-content}}>
          <GeneralChartsOtaChart
            {{! @glint-expect-error: Type '{ chart: { zoomType: null; type: string; title: string; height: null; ... }; ... }' is not assignable to type 'Options'. }}
            @chartOptions={{this.chartOptions}}
            @model={{this.chartData}}
            class="h-full"
          />
        </div>
      </:content>
    </PrismCard>
    <div class="hidden" id="dummy-tooltip-for-issue-type">
      <ParityTooltipsTooltipKpi
        @data={{this.tooltipValues}}
        @displayName={{this.tooltipValues.tooltipTitle}}
        @hideShoppedHotels={{@hideHotelsMetric}}
      />
    </div>
  </template>
}
